import clsx from 'clsx'
import type { ReactNode } from 'react'
import { View } from 'react-native'

import { Avatar } from '@/src/elements/avatar'
import { Button } from '@/src/elements/button/default'
import { BouncyChip } from '@/src/elements/chip/BouncyChip/BouncyChip'
import { Typography } from '@/src/elements/text/Typography'
import type { TUserAvatar } from '@/src/features/user/models/User'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'

export const prayerRowHeightClassName = 'h-[50px]'

type TPrayerRowProps = {
  index?: number
  hasThanked: boolean
  avatar: TUserAvatar | null
  isAnonymous: boolean
  userName: string
  hasUplift: boolean
  onThank?: VoidFunction
  onAnswerPress?: VoidFunction
  onSeeConversation: (conversationId: number) => void
  labels: {
    thankYouSent: string
    thank: string
    answer: string
    see: string
  }
  conversationId?: number
}

export const PrayerRow = ({
  avatar,
  userName,
  hasUplift,
  hasThanked,
  onThank,
  onAnswerPress,
  onSeeConversation,
  labels,
  isAnonymous,
  conversationId,
  index,
}: TPrayerRowProps): ReactNode => (
  <View
    testID={`prayer-row-${userName}`}
    className={clsx(
      'flex max-w-[400px] flex-row items-center gap-4',
      prayerRowHeightClassName,
      isAnonymous && 'opacity-50',
    )}
  >
    <Avatar size="md" src={getAvatarUrl(avatar)} />
    <Typography numberOfLines={1} bold className="flex-1">
      {userName}
    </Typography>
    {hasThanked && !isAnonymous && (
      <View className="flex flex-row items-center gap-1">
        <BouncyChip
          size="sm"
          className="flex-initial"
          text={labels.thankYouSent}
          iconName={ICON.CHECK}
          colors="success"
          variant="flat"
        />
        {conversationId && (
          <Button
            size="xs"
            variant="light"
            colors="primary"
            contrast="default"
            onPress={() => onSeeConversation(conversationId)}
          >
            {labels.see}
          </Button>
        )}
      </View>
    )}
    {!hasThanked && onThank && !hasUplift && (
      <Button size="sm" variant="faded" colors="primary" contrast="default" onPress={onThank}>
        {labels.thank}
      </Button>
    )}
    {!hasThanked && onAnswerPress && hasUplift && (
      <Button
        testID={TEST_ID.INTENTION.UPLIFT.ANSWER.BUTTON + `-${index}`}
        endIcon={ICON.CORNER_UP_LEFT_REPLY}
        size="sm"
        variant="faded"
        colors="primary"
        contrast="default"
        onPress={onAnswerPress}
      >
        {labels.answer}
      </Button>
    )}
  </View>
)
